/**
 * The Requests list: which rows show for a filter and search, in what order,
 * and what each row says (status tone, progress, last activity, the one
 * action that matters). Pure, so the list's rules are unit-testable.
 */
import type { OutgoingIntake } from "@/api/types/outgoingIntake";
import type { StatusTone } from "@/components/ui/status-badge";

import { trackProgress } from "../editor/trackProgress";
import { intakeActivity } from "../intakeActivity";
import {
  asDate,
  countSteps,
  displayStatus,
  intakeMeta,
  STAGES,
  stageOf,
  type Stage
} from "../model";

export type RequestsFilter = "all" | Stage;

export const REQUESTS_FILTERS: {
  value: Exclude<RequestsFilter, "all">;
  label: string;
}[] = STAGES.map((s) => ({ value: s.id, label: s.label }));

/** Needs the attorney first, then out with the client, then drafts. */
const LIST_ORDER: Stage[] = [
  "awaiting_review",
  "with_client",
  "drafts",
  "done",
  "expired"
];

const WITH_CLIENT = new Set(["sent", "in_progress", "revision_requested"]);

const updatedAt = (i: OutgoingIntake): number =>
  asDate(i.updated_at ?? i.created_at)?.getTime() ?? 0;

export const visibleRequests = (
  intakes: OutgoingIntake[],
  filter: RequestsFilter,
  query: string
): OutgoingIntake[] => {
  const q = query.trim().toLowerCase();
  return intakes
    .filter((i) => filter === "all" || stageOf(i.status) === filter)
    .filter(
      (i) =>
        !q ||
        [i.title, i.recipient_email, displayStatus(i), intakeMeta(i)]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(q))
    )
    .sort(
      (a, b) =>
        LIST_ORDER.indexOf(stageOf(a.status)) -
          LIST_ORDER.indexOf(stageOf(b.status)) || updatedAt(b) - updatedAt(a)
    );
};

export const statusTone = (intake: OutgoingIntake): StatusTone => {
  if (intake.cancelled_at) return "neutral";
  if (intake.link_revoked_at && WITH_CLIENT.has(intake.status)) return "warn";
  if (intake.email_bounced_at && WITH_CLIENT.has(intake.status))
    return "danger";
  switch (intake.status) {
    case "awaiting_review":
      return "accent";
    case "sent":
    case "in_progress":
      return "info";
    case "revision_requested":
      return "warn";
    case "accepted":
      return "success";
    default:
      return "neutral";
  }
};

export type RowProgress =
  | { kind: "size"; text: string }
  | { kind: "bar"; answered: number; total: number };

/** A draft's size, or how far the client got. */
export const progressOf = (intake: OutgoingIntake): RowProgress => {
  const schema = intake.schema ?? [];
  if (intake.status === "draft") {
    const n = countSteps(schema);
    return { kind: "size", text: `${n} ${n === 1 ? "question" : "questions"}` };
  }
  if (intake.status === "awaiting_review" || intake.status === "accepted") {
    const n = countSteps(schema);
    return { kind: "bar", answered: n, total: n };
  }
  const { answered, total } = trackProgress(
    schema,
    intake.progress_answers ?? [],
    WITH_CLIENT.has(intake.status)
  );
  return { kind: "bar", answered, total };
};

export type RowAction = "review" | "remind" | "open";

export const rowAction = (intake: OutgoingIntake): RowAction => {
  if (intake.status === "awaiting_review") return "review";
  if (
    WITH_CLIENT.has(intake.status) &&
    !intake.link_revoked_at &&
    !intake.cancelled_at
  )
    return "remind";
  return "open";
};

/** "Oct 2", with the year when it isn't this one. */
export const shortDate = (value: unknown, now: Date = new Date()): string => {
  const d = asDate(value);
  if (!d) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {})
  });
};

const ago = (date: Date, now: Date): string => {
  const minutes = Math.round((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return shortDate(date, now);
};

/** The latest thing that happened, as "Opened the link · 2h ago". */
export const lastActivity = (
  intake: OutgoingIntake,
  now: Date = new Date()
): { label: string; when: string } | null => {
  const past = intakeActivity(intake, [], now).filter((a) => !a.scheduled);
  const last = past[past.length - 1];
  if (last) return { label: last.label, when: ago(last.at, now) };
  const updated = asDate(intake.updated_at ?? intake.created_at);
  return updated
    ? {
        label: intake.status === "draft" ? "Edited" : "Updated",
        when: ago(updated, now)
      }
    : null;
};

const EMPTY_STAGE_TITLE: Record<Stage, string> = {
  awaiting_review: "No Requests Awaiting Review",
  with_client: "No Requests With Client",
  drafts: "No Draft Requests",
  done: "No Done Requests",
  expired: "No Expired Requests"
};

/** What the list says when a status or search hides every request. */
export const emptyMatchTitle = (
  filter: RequestsFilter,
  query: string
): string => {
  const q = query.trim();
  if (q) return `No Requests Match “${q}”`;
  return filter === "all" ? "No Matching Requests" : EMPTY_STAGE_TITLE[filter];
};

/** Suggestions for where this client's requests stand, most urgent first. */
export const starterPrompts = (
  name: string,
  intakes: OutgoingIntake[]
): string[] => {
  const live = (i: OutgoingIntake) => !i.cancelled_at && !i.link_revoked_at;
  const review = intakes.find((i) => i.status === "awaiting_review");
  const out = intakes.find((i) => WITH_CLIENT.has(i.status) && live(i));
  const draft = intakes.find((i) => i.status === "draft");
  const prompts = [
    review ? `Review “${review.title}” against ${name}'s record` : null,
    out ? `Remind ${name} about “${out.title}”` : null,
    out || review ? `What has ${name} not returned yet?` : null,
    draft ? `Finish the “${draft.title}” draft so it's ready to send` : null,
    intakes.length === 0
      ? `Build a biographical + documents intake for an H-1B`
      : null,
    intakes.length === 0 ? `Ask ${name} for a passport and I-94` : null,
    `What's missing from ${name}'s profile?`
  ].filter((p): p is string => Boolean(p));
  return prompts.slice(0, 4);
};
